// src/api/bespokeStaffService.js
import {
  collection,
  doc, 
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy
} from 'firebase/firestore';
import { 
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject
} from 'firebase/storage';
import { db, storage } from '../utils/firebase';

// Get all Bespoke staff members
export const getAllBespokeStaff = async () => {
  try {
    const staffQuery = query(
      collection(db, 'bespokeStaff'),
      orderBy('createdAt', 'desc')
    );
    
    const snapshot = await getDocs(staffQuery);
    const staffList = [];
    
    snapshot.forEach((docSnapshot) => {
      const data = docSnapshot.data();
      
      // CRITICAL: Explicitly extract ALL fields including URLs
      staffList.push({
        id: docSnapshot.id,
        name: data.name || '',
        title: data.title || '',
        email: data.email || '',
        phone: data.phone || '',
        address: data.address || '',
        logoUrl: data.logoUrl || '',
        logoUrlBack: data.logoUrlBack || '',
        qrCodeUrl: data.qrCodeUrl || '',
        createdAt: data.createdAt,
        updatedAt: data.updatedAt
      });
      
      // DEBUG: Log what we're getting from Firestore
      console.log('Staff member from Firestore:', {
        id: docSnapshot.id,
        name: data.name,
        logoUrl: data.logoUrl,
        logoUrlBack: data.logoUrlBack,
        qrCodeUrl: data.qrCodeUrl
      });
    });
    
    console.log('Total staff members loaded:', staffList.length);
    return staffList;
  } catch (error) {
    console.error('Error getting Bespoke staff:', error);
    throw error;
  }
};

// Get single staff member
export const getBespokeStaffMember = async (staffId) => {
  try {
    const docRef = doc(db, 'bespokeStaff', staffId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const data = docSnap.data();
      return {
        id: docSnap.id,
        name: data.name || '',
        title: data.title || '',
        email: data.email || '',
        phone: data.phone || '',
        address: data.address || '',
        logoUrl: data.logoUrl || '',
        logoUrlBack: data.logoUrlBack || '',
        qrCodeUrl: data.qrCodeUrl || '',
        createdAt: data.createdAt,
        updatedAt: data.updatedAt
      };
    }
    return null;
  } catch (error) {
    console.error('Error getting staff member:', error);
    throw error;
  }
};

// Create new staff member
export const createBespokeStaffMember = async (staffData) => {
  try {
    const docRef = doc(collection(db, 'bespokeStaff'));
    
    await setDoc(docRef, {
      name: staffData.name || '',
      title: staffData.title || '',
      email: staffData.email || '',
      phone: staffData.phone || '',
      address: staffData.address || '',
      logoUrl: staffData.logoUrl || '',
      logoUrlBack: staffData.logoUrlBack || '',
      qrCodeUrl: staffData.qrCodeUrl || '',
      createdAt: new Date(),
      updatedAt: new Date()
    });
    
    return docRef.id;
  } catch (error) {
    console.error('Error creating staff member:', error);
    throw error;
  }
};

// Update staff member
export const updateBespokeStaffMember = async (staffId, staffData) => {
  try {
    const docRef = doc(db, 'bespokeStaff', staffId);
    
    const updateData = {
      updatedAt: new Date()
    };
    
    // Only update fields that are provided
    if (staffData.name !== undefined) updateData.name = staffData.name;
    if (staffData.title !== undefined) updateData.title = staffData.title;
    if (staffData.email !== undefined) updateData.email = staffData.email;
    if (staffData.phone !== undefined) updateData.phone = staffData.phone;
    if (staffData.address !== undefined) updateData.address = staffData.address;
    if (staffData.logoUrl !== undefined) updateData.logoUrl = staffData.logoUrl;
    if (staffData.logoUrlBack !== undefined) updateData.logoUrlBack = staffData.logoUrlBack;
    if (staffData.qrCodeUrl !== undefined) updateData.qrCodeUrl = staffData.qrCodeUrl;
    
    await updateDoc(docRef, updateData);
    
    return true;
  } catch (error) {
    console.error('Error updating staff member:', error);
    throw error;
  }
};

// Delete staff member
export const deleteBespokeStaffMember = async (staffId) => {
  try {
    const docRef = doc(db, 'bespokeStaff', staffId);
    await deleteDoc(docRef);
    return true;
  } catch (error) {
    console.error('Error deleting staff member:', error);
    throw error;
  }
};

// Upload image to Firebase Storage
export const uploadStaffImage = async (staffId, file, imageType) => {
  try {
    const storageRef = ref(storage, `bespokestaff/${staffId}/${imageType}_${Date.now()}`);
    const snapshot = await uploadBytes(storageRef, file);
    const downloadURL = await getDownloadURL(snapshot.ref);
    return downloadURL;
  } catch (error) {
    console.error('Error uploading image:', error);
    throw error;
  }
};

// Delete image from Firebase Storage
export const deleteStaffImage = async (imageUrl) => {
  try {
    const imageRef = ref(storage, imageUrl);
    await deleteObject(imageRef);
    return true;
  } catch (error) {
    console.error('Error deleting image:', error);
    throw error;
  }
};